import { observable, action, computed, configure, runInAction } from "mobx";
import { createContext, SyntheticEvent, MouseEvent } from "react";
import { ITransmission as ITransmission } from "../models/transmission";
import { toast } from "react-toastify";
import agent from "../api/agent";
import { IProjectScope } from "../models/projectscope";
import { IWarehouseLog as IWarehouseLog } from "../models/warehouselog";
import { IProjectVendor } from "../models/projectvendor";
import { ITaskAssignment } from "../models/taskassignment";
import { ITechnician } from "../models/technician";
import { IProjectLog } from "../models/projectlog";
import { IProjectBog } from "../models/projectbog";
import { IProjectDocument } from "../models/projectdocument";

configure({ enforceActions: "always" });

class TransmissionStore {
  @observable transmissionRegistry = new Map();
  @observable transmissionSearchRegistry = new Map();
  @observable transmission: ITransmission | null = null;
  @observable projectscopeRegistry = new Map();
  @observable projectscope: IProjectScope | null = null;
  @observable selectedProjectScope: IProjectScope | undefined;
  @observable warehouselogRegistry = new Map();
  @observable warehouselog: IWarehouseLog | null = null;
  @observable warehouselogList: IWarehouseLog[] = new Array();
  @observable warehouseRegistry = new Map();
  @observable selectedWarehouselog: IWarehouseLog | undefined;
  @observable projectvendorRegistry = new Map();
  @observable projectvendor: IProjectVendor | null = null;
  @observable selectedProjectVendor: IProjectVendor | undefined;
  @observable technicianRegistry = new Map();
  @observable taskassignmentRegistry = new Map();
  @observable taskassignment: ITaskAssignment | null = null;
  @observable projectlogRegistry = new Map();
  @observable projectlog: IProjectLog | null = null;
  @observable projectbogRegistry = new Map();
  @observable projectdocumentRegistry = new Map();
  @observable activeDetailTab: number = 0;
  @observable activeListTab: number = 0;
  @observable loadingInitial = false;
  @observable submitting = false;
  @observable target = "";
  @observable editMode = false;
  @observable reload = false;

  @computed get transmissionsByDate() {
    return Array.from(this.transmissionSearchRegistry.values()).sort(
      (a, b) =>
        Date.parse(a.estimatedCompletionDate) -
        Date.parse(b.estimatedCompletionDate)
    );
  }

  @computed get projectScopesByDescription() {
    return Array.from(this.projectscopeRegistry.values()).sort((a, b) =>
      a.description.localeCompare(b.description)
    );
  }

  @computed get warehouselogsByName() {
    return Array.from(this.warehouselogRegistry.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }

  @computed get warehousesByName() {
    return Array.from(this.warehouseRegistry.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }

  @computed get projectbogsByTime() {
    return Array.from(this.projectbogRegistry.values()).sort(
      (a, b) => Date.parse(a.bogDate) - Date.parse(b.bogDate)
    )
  }

  @computed get projectDesignsByName() {
    return Array.from(this.projectdocumentRegistry.values()).filter((doc) =>
    doc.category === 'Design');
  }

  @computed get projectHOPsByName() {
    return Array.from(this.projectdocumentRegistry.values()).filter((doc) =>
    doc.category === 'HOP');
  }

  @action loadTransmissions = async () => {
    this.loadingInitial = true;
    try {
      const transmissions = await agent.Transmissions.list();
      runInAction("loading transmissions", () => {
        transmissions.forEach((transmission) => {
          transmission.estimatedCompletionDate
            ? (transmission.estimatedCompletionDate =
                transmission.estimatedCompletionDate.substr(0, 10))
            : (transmission.estimatedCompletionDate = "");
          this.transmissionRegistry.set(transmission.id, transmission);
          this.transmissionSearchRegistry.set(transmission.id, transmission);
        });
        this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load transmissions error", () => {
        this.loadingInitial = false;
      });
      toast.error("load transmissions error");
    }
  };

  @action loadTransmission = async (id: string) => {
    let transmission = this.getTransmission(id);
    if (transmission) {
      this.transmission = transmission;
      return transmission;
    } else {
      this.loadingInitial = true;
      try {
        transmission = await agent.Transmissions.details(id);

        runInAction("getting transmission", () => {
          if (transmission.estimatedCompletionDate) {
            transmission.estimatedCompletionDate =
              transmission.estimatedCompletionDate.substr(0, 10);
          }
          this.transmission = transmission;
          this.loadingInitial = false;
        });
        return transmission;
      } catch (error) {
        runInAction("get transmission error", () => {
          this.loadingInitial = false;
        });
        toast.error("get transmission error");
        console.log(error);
      }
    }
  };

  @action clearTransmission = () => {
    this.transmission = null;
  };

  getTransmission = (id: string) => {
    return this.transmissionRegistry.get(Number(id));
  };

  @action createTransmission = async (transmission: ITransmission) => {
    this.submitting = true;
    try {
      // transmission.createdAt = new Date().toISOString();
      // transmission.updatedAt = new Date().toISOString();
      transmission.createdAt = new Date().toJSON();
      transmission.updatedAt = new Date().toJSON();

      await agent.Transmissions.create(transmission);
      runInAction("create transmission", () => {
        // this.transmissionRegistry.set(transmission.id, transmission);
        this.submitting = false;
      });
    } catch (error) {
      runInAction("create transmission error", () => {
        this.submitting = false;
      });
      toast.error("create transmission error");
      console.log(error);
    }
  };

  @action editTransmission = async (transmission: ITransmission) => {
    this.submitting = true;
    try {
      await agent.Transmissions.update(transmission);
      runInAction("editing transmission", () => {
        this.transmissionRegistry.set(transmission.id, transmission);
        this.transmissionSearchRegistry.set(transmission.id, transmission);
        this.transmission = transmission;
        this.submitting = false;
      });
    } catch (error) {
      runInAction("edit transmission error", () => {
        this.submitting = false;
      });
      toast.error("edit transmission error");
      console.log(error);
    }
  };

  @action deleteTransmission = async (id: string) => {
    this.submitting = true;
    this.target = id;
    try {
      await agent.Transmissions.delete(id);
      runInAction("deleting transmission", () => {
        this.transmissionRegistry.delete(id);
        this.transmissionSearchRegistry.delete(id);
        this.submitting = false;
        this.target = "";
      });
    } catch (error) {
      runInAction("delete transmission error", () => {
        this.submitting = false;
        this.target = "";
      });
      toast.error("delete transmission error");
      console.log(error);
    }
  };

  @action generate = async () => {
    let code = "";
    try {
      var project = await agent.Transmissions.generate();
      code = project.projectCode;
      runInAction("getting code", () => {
        return code;
      });
    } catch (error) {
      runInAction("get code error", () => {});
      toast.error("get code error");
      console.log(error);
    }
    return code;
  };

  @action cancelFormOpen = () => {
    this.editMode = false;
  };

  // Project Scope
  @action loadProjectScopes = async (projectId: string) => {
    try {
      const projectscopes = await agent.ProjectScopes.list(projectId);
      this.projectscopeRegistry.clear();
      runInAction("loading project scopes", () => {
        projectscopes.forEach((projectscope) => {
          this.projectscopeRegistry.set(projectscope.id, projectscope);
        });
      });
    } catch (error) {
      runInAction("get project scope error", () => {});
      toast.error("get project scope error");
      console.log(error);
    }
  };

  @action createProjectScope = async (projectScope: IProjectScope) => {
    this.submitting = true;
    try {
      projectScope.createdAt = new Date().toJSON();
      projectScope.updatedAt = new Date().toJSON();

      await agent.ProjectScopes.create(projectScope);
      runInAction("create Project Scope", () => {
        this.submitting = false;
      });
    } catch (error) {
      runInAction("create project scope error", () => {
        this.submitting = false;
      });
      toast.error("create project scope error");
      console.log(error);
    }
  };

  @action editProjectScope = async (projectScope: IProjectScope) => {
    this.submitting = true;
    try {
      await agent.ProjectScopes.update(projectScope);
      runInAction("editing project scope", () => {
        this.projectscopeRegistry.set(projectScope.id, projectScope);
        this.projectscope = projectScope;
        this.editMode = false;
        this.submitting = false;
      });
    } catch (error) {
      runInAction("edit project scope error", () => {
        this.submitting = false;
      });
      toast.error("edit project scope error");
      console.log(error);
    }
  };

  @action deleteProjectScope = async (
    event: SyntheticEvent<HTMLButtonElement>,
    id: string
  ) => {
    this.submitting = true;
    this.reload = false;
    this.target = event.currentTarget.name;
    try {
      await agent.ProjectScopes.delete(id);
      runInAction("deleting project scope", () => {
        this.projectscopeRegistry.delete(id);
        this.submitting = false;
        this.reload = true;
        this.target = "";
      });
    } catch (error) {
      runInAction("delete project Scope error", () => {
        this.submitting = false;
        this.target = "";
      });
      toast.error("delete project Scope error");
      console.log(error);
    }
  };

  @action selectProjectScope = (id: string) => {
    this.selectedProjectScope = this.projectscopeRegistry.get(id);
    this.editMode = true;
  };

  //Material Tab

  @action loadWarehouses = async () => {
    this.loadingInitial = true;
    try {
      const warehouses = await agent.Warehouses.list();
      runInAction("loading warehouses", () => {
        warehouses.forEach((warehouse) => {
          if (warehouse.projectType === "5G Transmission") {
            this.warehouseRegistry.set(warehouse.id, warehouse);
          }
        });
        this.loadingInitial = false;
      });
    } catch (err) {
      runInAction("load Warehouse List error", () => {
        this.loadingInitial = false;
      });
      toast.error("load Warehouse List error");
    }
  };

  @action loadWarehouseLogs = async (projectId: string) => {
    try {
      const warehouselogs = await agent.WarehouseLogs.list(projectId);
      this.warehouselogRegistry.clear();
      runInAction("loading tasks", () => {
        warehouselogs.forEach((warehouselog) => {
          this.warehouselogRegistry.set(warehouselog.id, warehouselog);
        });
      });
    } catch (err) {
      toast.error("load tasks error");
    }
  };

  @action createWarehouseLog = async (warehouselogs: IWarehouseLog[]) => {
    this.submitting = true;
    try {
      await agent.WarehouseLogs.create(warehouselogs);
      runInAction("create warehouse log", () => {
        this.submitting = false;
      });
    } catch (error) {
      runInAction("create warehouse log error", () => {
        this.submitting = false;
      });
      toast.error("create warehouse log error");
      console.log(error);
    }
  };

  @action editWarehouselog = async (warehouselog: IWarehouseLog) => {
    this.submitting = true;
    try {
      await agent.WarehouseLogs.update(warehouselog);
      runInAction("editing warehouse log", () => {
        this.warehouselogRegistry.set(warehouselog.id, warehouselog);
        this.warehouselog = warehouselog;
        this.editMode = false;
        this.submitting = false;
      });
    } catch (error) {
      runInAction("edit warehouse log error", () => {
        this.submitting = false;
      });
      toast.error("edit warehouse log error");
      console.log(error);
    }
  };

  @action deleteWarehouseLog = async (
    event: SyntheticEvent<HTMLButtonElement>,
    id: string
  ) => {
    this.target = event.currentTarget.name;
    try {
      await agent.WarehouseLogs.delete(id);
      runInAction("deleting warehouse log", () => {
        this.warehouselogRegistry.delete(id);
        this.target = "";
      });
    } catch (error) {
      runInAction("delete warehouse log error", () => {
        this.target = "";
      });
      toast.error("delete warehouse log error");
      console.log(error);
    }
  };

  @action selectWarehouselog = (id: string) => {
    this.selectedWarehouselog = this.warehouselogRegistry.get(id);
    this.editMode = true;
  };

  // Project Vendor
  @action createProjectVendor = async (projectvendor: IProjectVendor) => {
    this.submitting = true;
    try {
      projectvendor.createdAt = new Date().toJSON();
      projectvendor.updatedAt = new Date().toJSON();
      console.log(projectvendor);
      await agent.ProjectVendors.create(projectvendor);
      runInAction("create project vendor", () => {
        this.submitting = false;
      });
    } catch (error) {
      runInAction("create project vendor error", () => {
        this.submitting = false;
      });
      console.log(error);
    }
  };

  @action loadProjectVendors = async (projectId: string) => {
    try {
      const projectvendors = await agent.ProjectVendors.list(projectId);
      this.projectvendorRegistry.clear();
      runInAction("loading vendors", () => {
        projectvendors.forEach((projectvendor) => {
          this.projectvendorRegistry.set(projectvendor.id, projectvendor);
        });
      });
    } catch (error) {
      runInAction("load vendors error", () => {});
    }
  };

  @action deleteProjectVendor = async (
    event: SyntheticEvent<HTMLButtonElement>,
    id: string
  ) => {
    this.submitting = true;
    this.reload = false;
    this.target = event.currentTarget.name;
    try {
      await agent.ProjectVendors.delete(id);
      runInAction("deleting project vendor", () => {
        this.projectvendorRegistry.delete(id);
        this.submitting = false;
        this.reload = true;
        this.target = "";
      });
    } catch (error) {
      runInAction("delete project vendor error", () => {
        this.submitting = false;
        this.target = "";
      });
      console.log(error);
    }
  };

  @action editProjectVendor = async (projectvendor: IProjectVendor) => {
    this.submitting = true;
    try {
      await agent.ProjectVendors.update(projectvendor);
      runInAction("editing project vendor", () => {
        this.projectvendorRegistry.set(projectvendor.id, projectvendor);
        this.projectvendor = projectvendor;
        this.editMode = false;
        this.submitting = false;
      });
    } catch (error) {
      runInAction("edit project error", () => {
        this.submitting = false;
      });
      console.log(error);
    }
  };

  @computed get projectvendorsByName() {
    return Array.from(this.projectvendorRegistry.values()).sort((a, b) =>
      a.companyName.localeCompare(b.companyName)
    );
  }

  //Technician Tab
  @computed get techniciansByName() {
    return Array.from(this.technicianRegistry.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }

  @action loadTechnicians = async () => {
    try {
      const employees = await agent.Employees.list();
      runInAction("loading empolyees", () => {
        this.technicianRegistry.clear();
        employees.forEach((employee) => {
          if ((employee.status === 'Employee' || employee.status === 'Subcontractor') && 
          employee.projectType.includes('Transmission')) 
          {
            this.technicianRegistry.set(employee.email, employee);
          }
        });
      });
    } catch (error) {
      runInAction("load technician Error", () => {
      });
      toast.error("load technician Error");
    }
  };

  @action loadTaskAssignments = async (projectId: string) => {
    // this.loadingInitial = true;
    try {
      const taskassignments = await agent.TaskAssignments.list(projectId);
      this.taskassignmentRegistry.clear();
      runInAction("loading tasks", () => {
        taskassignments.forEach((taskassignment) => {
          this.taskassignmentRegistry.set(
            taskassignment.technicianEmail,
            taskassignment
          );
        });
        // this.loadingInitial = false;
      });
    } catch (error) {
      runInAction("load tasks error", () => {
        // this.loadingInitial = false;
      });
      toast.error("load tasks error");
    }
  };

  @action creatTechnicianTask = async (task: ITaskAssignment) => {
    this.submitting = true;
    if (task.technicianEmail === '')
    {
      toast.error("Please select technician");
      this.submitting = false;
      return;
    }
    else
    {
      if (this.taskassignmentRegistry.has(task.technicianEmail))
      {
        toast.error("This technician is already assigned");
        this.submitting = false;
        return;
      }
    }
    try {
      task.createdAt = new Date().toJSON();
      task.updatedAt = new Date().toJSON();

      await agent.TaskAssignments.create(task);

      runInAction("Create Technician Task", () => {
        this.submitting = false;
      });
    } catch (error) {
      runInAction("create Technician Task error", () => {
        this.submitting = false;
      });
      toast.error("create Technician Task error");
      console.log(error);
    }
  };

  @action deleteTechnicianTask = async (
    event: SyntheticEvent<HTMLButtonElement>,
    tech: ITechnician
  ) => {
    this.submitting = true;
    this.target = event.currentTarget.name;
    this.reload = false;
    const selectedTask = this.taskassignmentRegistry.get(tech.email);
    try {
      await agent.TaskAssignments.delete(selectedTask.id);
      runInAction("deleting Techncian task", () => {
        this.taskassignmentRegistry.delete(selectedTask.technicianEmail);
        this.submitting = false;
        this.target = "";
        this.reload = true;
      });
    } catch (error) {
      runInAction("delete Techncian task error", () => {
        this.submitting = false;
        this.target = "";
      });
      toast.error("delete Techncian task error");
      console.log(error);
    }
  };

  @computed get taskassignmentsByCategory() {
    return Array.from(this.taskassignmentRegistry.values()).sort((a, b) =>
      a.itemCategory.localeCompare(b.itemCategory)
    );
  }

  //Project Log Tab
  @action loadProjectLogs = async (projectId: string) => {
    try {
      const projectlogs = await agent.ProjectLogs.list(projectId);
      this.projectlogRegistry.clear();
      runInAction('loading tasks', () => {
        projectlogs.forEach(projectlog => {
          this.projectlogRegistry.set(projectlog.id, projectlog);
        });
      })
  
    } catch (error) {
      runInAction('load tasks error', () => {
      })
      toast.error('load tasks error');
    }
  };
  
  @action createProjectLog = async (projectlog: IProjectLog) => {
    this.submitting = true;
    try {
     projectlog.createdAt = new Date().toJSON();
     projectlog.updatedAt = new Date().toJSON();
  
      await agent.ProjectLogs.create(projectlog);
      runInAction('create project log', () => {
        this.submitting = false;
      })
    } catch (error) {
      runInAction('create project log error', () => {
        this.submitting = false;
      })
      toast.error('create project log error');
      console.log(error);
    }
  };
  
  @action deleteProjectLog = async (event: SyntheticEvent<HTMLButtonElement>, id: string) => {
    this.target = event.currentTarget.name;
    try {
      await agent.ProjectLogs.delete(id);
      runInAction('deleting project', () => {
        this.projectlogRegistry.delete(id);
        this.target = '';
      })
    } catch (error) {
      runInAction('delete project error', () => {
        this.target = '';
      })
      toast.error('delete project error');
      console.log(error);
    }
  }

  @computed get projectlogsByDate() {
    return Array.from(this.projectlogRegistry.values()).sort(
      (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)
    );
  }

  @action setActiveDetailTab = (activeIndex: number) => {
    this.activeDetailTab = activeIndex;
  } 

  @action setActiveListTab = (activeIndex: number) => {
    this.activeListTab = activeIndex;
  }
  
  //BOG
  @action loadProjectBogs = async (projectId: string) => {
    try {
      const projectbogs = await agent.ProjectBogs.list(projectId);
      this.projectbogRegistry.clear();
      runInAction('loading bogs', () => {
        if (projectbogs.length === 0)
        {
          this.projectbogRegistry.set('new', {createdAt: new Date().toISOString(), activity: 'new'});
        }
        else
        {
          projectbogs.forEach(projectbog => {
            projectbog.bogDate = projectbog.bogDate.substr(0,10);
            projectbog.technicianEmail = projectbog.technicianEmail.split(',');
            this.projectbogRegistry.set(projectbog.id, projectbog);
          });
        };
      })
  
    } catch (error) {
      runInAction('load bogs error', () => {
      })
      toast.error('load bogs error');
    }
  };
  
  @action createProjectBog = async (projectbog: IProjectBog) => {
    this.submitting = true;
    projectbog.technicianEmail = projectbog.technicianEmail.toString();
    try {
      const bog = await agent.ProjectBogs.create(projectbog);
      runInAction('create project bog', () => {
        this.projectbogRegistry.delete('new');
        bog.bogDate = bog.bogDate.substr(0,10)
        bog.technicianEmail = bog.technicianEmail.split(',');
        this.projectbogRegistry.set(bog.id, bog);
        this.submitting = false;
      })
    } catch (error) {
      runInAction('create project bog error', () => {
        this.submitting = false;
      })
      toast.error('create project bog error');
      console.log(error);
    }
  };
  
  @action deleteProjectBog = async (id: string) => {
    try {
      await agent.ProjectBogs.delete(id);
      runInAction('deleting project bog', () => {
        this.projectbogRegistry.delete(id);
      })
    } catch (error) {
      runInAction('delete project bog error', () => {
      })
      toast.error('delete project bog error');
      console.log(error);
    }
  }

  @action addProjectBog = async () => {
    this.projectbogRegistry.set('new', {createdAt: new Date().toISOString(), activity: 'new'});
  }

  @action updateBog = async (task : IProjectBog, name: keyof IProjectBog, value: any) => {
    task[name] = value as never;
    this.projectbogRegistry.set(task.id, task);
  };

  @action editProjectBog = async (projectbog: IProjectBog) => {
    this.submitting = true;
    projectbog.technicianEmail = projectbog.technicianEmail.toString();
    try {
      await agent.ProjectBogs.update(projectbog);
      runInAction('editing project', () => {
        projectbog.technicianEmail = projectbog.technicianEmail.split(',');
        this.projectbogRegistry.set(projectbog.id, projectbog);
        this.submitting = false;
      })
      toast.success('Saved successfully');

    } catch (error) {
      runInAction('edit project bog error', () => {
        this.submitting = false;
      })
      toast.error('edit project bog error');
      console.log(error);
    }
  };

  @action searchTransmission = async (term: string) => {
    let transmissions = Array.from(this.transmissionRegistry.values());
    if (term) {
      const selectedTransmissions = transmissions.filter((transmission) =>
        transmission.siteId.toLowerCase().includes(term.toLowerCase()) || 
        transmission.siteName.toLowerCase().includes(term.toLowerCase())
      );
      this.transmissionSearchRegistry.clear();
        selectedTransmissions.forEach((transmission) => {
          this.transmissionSearchRegistry.set(transmission.id, transmission);
        });
    } else {
      this.transmissionSearchRegistry.clear();
      transmissions.forEach((transmission) => {
         this.transmissionSearchRegistry.set(transmission.id, transmission);
      });
    }
  };

  //Project Documents
  @action loadProjectDocuments = async (projectId: string) => {
    try {
      const projectdocuments = await agent.ProjectDocuments.list(projectId);
      this.projectdocumentRegistry.clear();
      runInAction('loading documents', () => {
        projectdocuments.forEach(projectdocument => {
          this.projectdocumentRegistry.set(projectdocument.id, projectdocument);
        });
      })
  
    } catch (error) {
      runInAction('load documents error', () => {
      })
      toast.error('load documents error');
    }
};
  
@action createProjectDocuments  = async (projectdocuments: IProjectDocument) => {
    this.submitting = true;
    try {
      const docs = await agent.ProjectDocuments.create(projectdocuments);
      runInAction('create documents', () => {
        this.projectdocumentRegistry.set(docs.id, docs);
        this.submitting = false;
      })
    } catch (error) {
      runInAction('create documents error', () => {
        this.submitting = false;
      })
      toast.error('create documents error');
      console.log(error);
    }
};
  
@action deleteProjectDocument = async (event: SyntheticEvent<HTMLButtonElement>, id: string) => {
    this.target = event.currentTarget.name;
    try {
      await agent.ProjectDocuments.delete(id);
      runInAction('deleting project', () => {
        this.projectdocumentRegistry.delete(id);
        this.target = '';
      })
    } catch (error) {
      runInAction('delete project error', () => {
        this.target = '';
      })
      toast.error('delete project error');
      console.log(error);
    }
}

  @action clearPage = () => {
    this.projectlogRegistry.clear();
    this.technicianRegistry.clear();
    this.projectvendorRegistry.clear();
    this.projectscopeRegistry.clear();
    this.transmission = null;
    this.warehouselogList.length = 0;
    this.projectscope = null;
    this.projectvendor = null;
    this.warehouselog = null;
    this.warehouselogRegistry.clear();
    this.warehouseRegistry.clear();
    this.taskassignmentRegistry.clear();
    this.taskassignment = null;
  }
  
}

export default createContext(new TransmissionStore());
